import { h } from "preact";
import { useState, useEffect } from "preact/hooks";

import { clamp } from "../../../foundation/utils/math";
import { getVerticalScroll, getHorizontalScroll } from "../utils/scroll";
import Portal from "../../Portal";
import { MouseOrigin } from "../types";

import styles from "./Selectbox.module.css";

interface SelectboxProps {
    mouseDownOrigin: MouseOrigin;
    scrollContainer: HTMLElement;
    maxScrollTop: number;
    maxScrollLeft: number;
    onRectUpdate: (selectBoxRect: SelectboxRect) => void;
}

interface SelectboxRect {
    x: number;
    y: number;
    width: number;
    height: number;
}

const MIN_SIZE = 1;

function Selectbox({
    scrollContainer,
    maxScrollTop,
    maxScrollLeft,
    mouseDownOrigin,
    onRectUpdate
}: SelectboxProps) {
    const [rect, setRect] = useState<SelectboxRect | null>(null);

    const fallbackRect = {
        x: mouseDownOrigin.x,
        y: mouseDownOrigin.y,
        width: MIN_SIZE,
        height: MIN_SIZE
    };

    useEffect(() => {
        if (!rect) {
            onRectUpdate(fallbackRect);
        }

        let requestedScroll: ReturnType<typeof requestAnimationFrame> | null = null;

        function updateSelectboxRect(event: MouseEvent) {
            const { clientX, clientY } = event;
            const scrollBounds = scrollContainer.getBoundingClientRect();

            const pointX = clamp(
                clientX - scrollBounds.left + scrollContainer.scrollLeft,
                0,
                scrollBounds.width + scrollContainer.scrollLeft
            );
            const selectboxX = Math.min(pointX, mouseDownOrigin.x);

            const pointY = clamp(
                clientY - scrollBounds.top + scrollContainer.scrollTop,
                0,
                scrollBounds.height + scrollContainer.scrollTop
            );
            const selectboxY = Math.min(pointY, mouseDownOrigin.y);

            const updatedRect = {
                x: selectboxX,
                y: selectboxY,
                width: Math.abs(pointX - mouseDownOrigin.x),
                height: Math.abs(pointY - mouseDownOrigin.y)
            };

            setRect(updatedRect);
            onRectUpdate(updatedRect);
        }

        function updateContainerScroll(event: MouseEvent, scrollBounds: DOMRect) {
            const { clientX, clientY } = event;

            const updatedScrollTop = getVerticalScroll({
                clientY,
                currentTop: scrollContainer.scrollTop,
                topBound: scrollBounds.top,
                bottomBound: scrollBounds.bottom,
                maxScrollTop
            });

            const updatedScrollLeft = getHorizontalScroll({
                clientX,
                currentLeft: scrollContainer.scrollLeft,
                leftBound: scrollBounds.left,
                rightBound: scrollBounds.right,
                maxScrollLeft
            });

            const shouldUpdateScrollTop = updatedScrollTop !== null &&
                updatedScrollTop !== scrollContainer.scrollTop;

            const shouldUpdateScrollLeft = updatedScrollLeft !== null &&
                updatedScrollLeft !== scrollContainer.scrollLeft;

            if (shouldUpdateScrollTop) {
                scrollContainer.scrollTop = updatedScrollTop!;
            }

            if (shouldUpdateScrollLeft) {
                scrollContainer.scrollLeft = updatedScrollLeft!;
            }

            return shouldUpdateScrollTop || shouldUpdateScrollLeft;
        }

        function handleMouseMove(event: MouseEvent) {
            const scrollBounds = scrollContainer.getBoundingClientRect();

            let containerScrolled = updateContainerScroll(event, scrollBounds);
            updateSelectboxRect(event);

            if (requestedScroll) {
                cancelAnimationFrame(requestedScroll);
                requestedScroll = null;
            }

            if (!containerScrolled) {
                return;
            }

            const scrollWithAnimation = () => {
                requestedScroll = requestAnimationFrame(() => {
                    containerScrolled = updateContainerScroll(event, scrollBounds);

                    if (containerScrolled) {
                        updateSelectboxRect(event);
                        scrollWithAnimation();
                    }
                });
            };

            scrollWithAnimation();
        }

        document.addEventListener("mousemove", handleMouseMove);
        document.body.classList.add("selecting");

        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.body.classList.remove("selecting");

            if (requestedScroll) {
                cancelAnimationFrame(requestedScroll);
                requestedScroll = null;
            }
        };
    }, [mouseDownOrigin]);

    const renderRect = rect ?? fallbackRect;

    const style = {
        left: renderRect.x,
        top: renderRect.y,
        width: Math.max(renderRect.width, MIN_SIZE),
        height: Math.max(renderRect.height, MIN_SIZE)
    };

    return (
        <Portal into={scrollContainer}>
            <div class={styles.selectbox} style={style}/>
        </Portal>
    );
}

export { Selectbox, SelectboxRect };
